import { Injectable } from '@angular/core';
import { BUSINESS_CONST } from '../../constants/business.constants';
import { IFirmlyApproved } from '../../interfaces/firmly-approved';
import { OfferModel } from '../../models/offer.model';
import { DetailProductSpecialOffer, SpecialOfferModel } from '../../models/specialOffer/special-offer.model';
import { ParametersService } from '../parameters/parameters.service';
import { RequestService } from '../request/request-onboarding.service';

/**
 * Servicio para los servicios de aprobados en firme
 * ofrecido por el banco.
 * @Injectable
 */
@Injectable({
  providedIn: 'root',
})
export class FirmlyApprovedService {
  /**
   * Contiene los datos de la vitrina seleccionada por el usuario
   * en el Home de App y Web.
   */
  private productsFirmlyApproved: IFirmlyApproved[];

  /**
   * Datos de la oferta que se recibe desde el chasis del canal
   * y corresponde a las ofertas de Aprobados en Firme.
   */
  private offer_channel = {
    offerType: '',
    offerId: '',
  };

  /**
   * Datos detallados dela TC del Aprobado En Firme
   * conforme lo selecciona el usuario en la etapa de
   * Configuración del producto
   */
  private detailProductsMulti: DetailProductSpecialOffer[] = [];

  /**
   * Indicador si el flujo va por Mono (1 solo producto) o
   * Multi (2 o más productos)
   */
  private multiproducto_Signal = false;

  /**
   * Constructor del servicio de Aprobados en Firme y Pre-Aprobados.
   * @param parametersService
   * @param abreCuentasService
   */
  constructor(
    private parametersService: ParametersService,
    private requestService: RequestService,
  ) {
  }

  /**
   * Función que permite modificar la señal de flujo Multiproducto
   * @param {boolean} status - Estado a ingresar
   */
  setMultiproducto_Signal(status: boolean) {
    this.multiproducto_Signal = status;
  }

  /**
   * Función quer permite obtener la señal si el flujo
   * es Multiproducto o Monoproducto
   * @returns {boolean} - Señal flujo Multi
   */
  getMultiproducto_Signal(): boolean {
    return this.multiproducto_Signal;
  }

  /**
   * Función que edita el valor del offerId recibido desde el
   * chasis del canal.
   * @param {String} offerId - Id de la oferta a consultar.
   */
  setOffer_Channel(offerDataChannel: SpecialOfferModel) {
    this.offer_channel = offerDataChannel;
  }

  /**
   * Función que devuelve el valor del OfferId.
   * @returns {String} - Valor del Id de las ofertas a consultar.
   */
  getOfferId_Channel(): string {
    return this.offer_channel.offerId;
  }

  /**
   * Función que devuelve el valor del OfferId.
   * @returns {String} - Tipo de las ofertas a consultar.
   */
  getOfferType_Channel(): string {
    return this.offer_channel.offerType;
  }

  /**
   * Función que permite ir añadiendo los productos individualmente al array
   * de productos de Aprobados en Firme
   * @param {IFirmlyApproved} offer - Oferta de Aprobado en Firme
   */
  addOffer(offer: IFirmlyApproved) {
    offer.product_type = this.getSelectProductFirmlyApproved(offer.product);
    this.productsFirmlyApproved.push(offer);
  }

  /**
   * Función que permite almacenar los datos que llegan desde vitrinas
   * y transversal al flujo de Aprobados en Firme y Pre-Aprobados.
   * @param {IFirmlyApproved} product - Datos obtenidos de la vitrina
   */
  setProductsFirmlyApproved(products: IFirmlyApproved[]) {
    this.productsFirmlyApproved = products;
  }

  /**
   * Función que permite enviar todo el objeto completo de FirmlyApproved
   * @returns {IFirmlyApproved} - Devuelve el objeto completo
   */
  getProductsFirmlyApproved(): IFirmlyApproved[] {
    return this.productsFirmlyApproved;
  }

  /**
   * Función que permite llenar los datos de los productos del Aprobado
   * En Firme y que reemplaza validaciones de PCO
   * @param {string} product_type - Producto a ingresar información
   */
  setDetailProductsMulti(product_type: string, cant_TCR?: number) {
    product_type = this.getSelectProductFirmlyApproved(product_type);
    const product_selected = this.getProduct_FirmlyApproved(product_type);
    let detail_exist = this.detailProductsMulti === undefined ? null : this.detailProductsMulti.find(element => element.product_type === product_type);
    if (!product_selected || detail_exist) {
      return;
    }
    let cant_ingreso = cant_TCR || 1;
    for (let index = 0; index < cant_ingreso; index++) {
      const product = {
        brand: '',
        product_type,
        limit_amount: {
          currency: 'COP',
          amount: parseInt(product_selected.amount, 10),
          decimals: 0,
        },
        term: product_selected.maxTermFreeInvestment,
      };
      this.detailProductsMulti.push(product);
    }
  }

  /**
   * Función que permite eliminar un producto del conjunto
   * de productos seleccionados por el cliente.
   * @param {string} product_type - Tipo del producto a eliminar
   */
  delete_DetailProductMulti(product_type: string) {
    this.detailProductsMulti = this.detailProductsMulti.filter(product => product.product_type !== product_type);
    console.log('Nuevo array de Productos Multi Aprobados Firme: ', this.detailProductsMulti);
  }

  /**
   * Función que devuelve los detalles de la TC y que se reemplaza
   * por algunos modelos de PCO del Onboarding normal.
   * @param {string} product_type - producto a obtener información
   * @returns {any} - Detalles de la TC
   */
  getDetailProductsMulti(product_type?: string): any {
    console.log('Producto a buscar: ', product_type, '\nDetalles encontrados: ', this.detailProductsMulti);
    if (!product_type) {
      return this.detailProductsMulti;
    }
    return this.detailProductsMulti.filter(element => element.product_type === product_type);
  }

  /**
   * Función que permite setear la selección del tipo de TC seleccionado por
   * el usuario y que es de Aprobados En Firme el producto
   * @param {number} position - Posición del brand seleccionado
   * @returns {string} - Nombre del Brand de la TC
   */
  selectedBrand(position: number): string {
    const brand = {
      1: () => BUSINESS_CONST.MASTERCARD,
      2: () => BUSINESS_CONST.VISA,
      3: () => BUSINESS_CONST.VISALM,
      default: () => '',
    };
    return brand[position] ? brand[position]() : brand.default();
  }

  /**
   * Función que permite validar si el cliente optó por el producto especial ofrecido por el banco.
   * @returns {boolean} - False - Tiene 1 o más productos de Aprobados en Firme, True - No cuenta con el producto especial.
   */
  isEmpty(): boolean {
    return this.productsFirmlyApproved === undefined ||
      this.productsFirmlyApproved.length === 0
      ? true
      : false;
  }

  /**
   * Función que devuelve el producto correspondiente al aprobado en firme
   * @returns {string} - Nombre del producto correspondiente al aprobado en firme.
   */
  getSelectProductFirmlyApproved(product: string): string {
    console.log('product ->', product);
    const productApproved = {
      ORD: () => 'freeInvestment',
      TCR: () => 'creditCard',
      CRP: () => 'revolvingCredit',
      MULTI: () => 'multi',
      default: () => product,
    };

    return productApproved[product]
      ? productApproved[product]()
      : productApproved.default();
  }

  /**
   * Función que permite obtener un producto en específico de los listados
   * en el Aprobado en Firme.
   * @param {string} product - Tipo del producto a buscar (ORD, TCR, CRP)
   * @returns - Producto encontrado.
   */
  getProduct_FirmlyApproved(product: string) {
    product = this.getSelectProductFirmlyApproved(product);
    return this.productsFirmlyApproved.find(element => (element.product_type === product));
  }

  /**
   * Función que permite cargar las ofertas de Aprobado En Firme Multiproducto para el cliente
   * @param {String} offer_id - Id de la oferta que se cargó en el home de los canales.
   */
  async getProductsOffer() {
    await this.requestService
      .getOffersFromServer(this.offer_channel.offerId || undefined)
      .then(async (res: any) => {
        if (res.data && res.data.length > 0) {
          this.productsFirmlyApproved = [];
          await res.data.map(offer => {
            if (offer.offer_status == '0') {
              this.addOffer(new OfferModel(offer));
            }
          });
        }
        // console.log('Datos de Aprobados Firme >>>>> \n', this.getProductsFirmlyApproved());
      })
      .catch(err => {
        console.warn('El cliente no cuenta con ofertas de aprobados en firme ->', err);
      });
  }

  /**
   * Función que permite actualizar el estado de una Oferta y sus productos.
   * @param {number} status_id - Estado para actualizar la oferta de la vitrina
   * @param {[any]} product_list - Ofertas a modificar
   */
  turnOffOffer(status_id: number, product_list?: [any]) {
    const offer_list: [SpecialOfferModel] = [this.offer_channel];

    this.requestService
      .updateOffer(status_id, offer_list, product_list)
      .then((res: any) => {
        this.productsFirmlyApproved = [];
        console.log('Datos de Aprobados Firme >>>>> \n', res);
      });
  }

  /**
   * Función que valida y retorna las solicitudes de un cliente que se encuetran en zona gris (cumplimiento o credito)
   * @returns {[Object]} - Array de solicitudes
   */
  async validateRequestsGZ(documentId: string) {
    const resObject: any = {};
    // cadena de estados de zona gris de credito y de cumplimiento
    const creditGrayZone =
      this.parametersService.getBusinessRule(
        'states_gray_zone',
      ).credit_gray_zone;
    const complianceGrayZone =
      this.parametersService.getBusinessRule(
        'states_gray_zone',
      ).compliance_gray_zone;
    const productCodesValidateGZ = this.parametersService.getBusinessRule(
      'product_codes_validate_gz_af',
    );
    // Validacion de fecha: Hace un mes
    const oneMonthAgo = new Date();
    if (oneMonthAgo.getMonth() === 0) {
      oneMonthAgo.setFullYear(oneMonthAgo.getFullYear() - 1);
      oneMonthAgo.setMonth(11);
    } else {
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    }
    // Consulta al canal de las solicitudes de un cliente por numero de identificacion.
    await this.requestService
      .getChannelNotificationClient(documentId)
      .then((res: any) => {
        const dataResponse = res.data;
        const visibleSolicitations: any[] = [];
        dataResponse.forEach(solicitation => {
          const stateSolicitation =
            solicitation.solicitation_phase +
            '-' +
            solicitation.solicitation_status;
          const isInCreditGrayZone = creditGrayZone.includes(stateSolicitation);
          const isInComplianceGrayZone =
            complianceGrayZone.includes(stateSolicitation);
          const dateRequest = new Date(solicitation.last_update_date);

          if (
            (isInCreditGrayZone || isInComplianceGrayZone) &&
            dateRequest >= oneMonthAgo &&
            solicitation.products.some(
              (product: any) =>
                productCodesValidateGZ.indexOf(product.product_type) >= 0,
            )
          ) {
            visibleSolicitations.push(solicitation);
          }
        });
        resObject.code = '200';
        resObject.data = visibleSolicitations;
      })
      .catch(error => {
        console.error(error);
        resObject.code = '107';
        resObject.data = [{}];
      });
    return resObject;
  }

  isFirmlyApproved() {
    return !this.isEmpty() && this.getOfferType_Channel() === 'En_firme';
  }
}
